import { useCallback, useEffect, useState } from 'react';
import { useSyncedRef } from 'shared/hooks/useSyncedRef';
import { BadgeModes } from 'types/badge-modes';

interface ProfileOption {
  value: string;
  label: string;
  score: number;
  lastUpdated: Date;
  topEmoji: string;
}

interface UseKeyboardNavigationArgs {
  /** Whether the dropdown menu is currently open. */
  menuIsOpen: boolean;
  /** Current app mode. */
  mode: BadgeModes;
  setMode: (mode: BadgeModes) => void;
  /** Currently selected profile identifier. */
  selectedProfileID: string | null;
  /** Sorted list of profile options (rank order). */
  options: ProfileOption[];
  /** Handler called when a profile option is selected. */
  handleOnChange: (option: { value: string | null } | null) => void;
  /** When false, listeners stay registered but do nothing (avoids double-binding with legacy chrome). */
  enabled?: boolean;
}

/**
 * Adds global arrow-key listeners for navigating between profiles by rank.
 * Arrow Up / Left → previous (wraps from #1 to last);
 * Arrow Down / Right → next (wraps from last to #1).
 */
export const useKeyboardNavigation = ({
  menuIsOpen,
  mode,
  setMode,
  selectedProfileID,
  options,
  handleOnChange,
  enabled = true,
}: UseKeyboardNavigationArgs) => {
  const [buttonPressed, setButtonPressed] = useState(false);
  const buttonPressedRef = useSyncedRef(buttonPressed);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled) return;
      if (menuIsOpen) return;
      if (mode === 'editing_profile') return;
      if (options.length === 0) return;

      if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
      }

      const goPrev = e.key === 'ArrowUp' || e.key === 'ArrowLeft';
      const goNext = e.key === 'ArrowDown' || e.key === 'ArrowRight';
      if ((!goPrev && !goNext) || buttonPressedRef.current) return;

      setButtonPressed(true);

      const currentIndex = selectedProfileID ? options.findIndex((option) => option.value === selectedProfileID) : -1;

      let nextIndex: number;
      if (goPrev) {
        if (currentIndex <= 0) nextIndex = options.length - 1;
        else nextIndex = currentIndex - 1;
      } else if (currentIndex < 0 || currentIndex >= options.length - 1) {
        nextIndex = 0;
      } else {
        nextIndex = currentIndex + 1;
      }

      if (mode !== 'collected' && mode !== 'submitted' && mode !== 'journeys' && mode !== 'profile_feed') {
        setMode('collected');
      }
      handleOnChange(options[nextIndex]);
    },
    [enabled, menuIsOpen, mode, setMode, buttonPressedRef, selectedProfileID, handleOnChange, options]
  );

  const handleKeyUp = useCallback(
    (_e: KeyboardEvent) => {
      if (buttonPressedRef.current === true) {
        setButtonPressed(false);
      }
    },
    [buttonPressedRef]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);
};
